<template>
  <section class="contact-form lavendla-tombstone-form light-form">
    <h3 v-text="tombstoneTexts.heading" />

    <transition name="contact-form__reveal-error">
      <p v-if="hasErrors" class="errors" v-html="error" />
    </transition>

    <transition name="contact-form__fade" mode="out-in">
      <form
        v-if="!formSent"
        method="post"
        :name="formName"
        @submit.prevent="sendInquiry()"
      >
        <form-input
          ref="name"
          v-model="name"
          :label="texts.name"
          :type="'text'"
          :name="'name'"
          :required="true"
          :autocomplete="'name'"
          @trigger-field-error="triggerFieldError"
        />

        <form-input
          ref="email"
          v-model="email"
          :label="texts.email"
          :type="'email'"
          :name="'email'"
          :required="true"
          :autocomplete="'email'"
          :check-email-on-blur="true"
          @trigger-field-error="triggerFieldError"
        />

        <form-input
          ref="phone"
          v-model="phone"
          :label="texts.phone"
          :type="'tel'"
          :name="'phone'"
          :required="true"
          :autocomplete="'phone'"
          @trigger-field-error="triggerFieldError"
        />

        <form-input
          ref="deceasedName"
          v-model="deceasedName"
          :label="texts.deceasedName"
          :type="'text'"
          :name="'deceasedName'"
          :autocomplete="'deceasedName'"
          @trigger-field-error="triggerFieldError"
        />

        <!-- TODO: Remove these or make them work with different dates. Number is not a good input form type -->
        <!-- <form-input
          ref="birthYear"
          v-model="birthYear"
          :label="texts.birthYear"
          :type="'number'"
          :name="'birthYear'"
          :autocomplete="'birthYear'"
          @trigger-field-error="triggerFieldError"
        />

        <form-input
          ref="deathYear"
          v-model="deathYear"
          :label="texts.deathYear"
          :type="'number'"
          :name="'deathYear'"
          :autocomplete="'deathYear'"
          @trigger-field-error="triggerFieldError"
        /> -->

        <div class="checkbox contact-form__accept">
          <input
            id="contact-form-tombstone__accept"
            v-model="accept"
            type="checkbox"
            required
          />
          <label class="marker" for="contact-form-tombstone__accept">
            <span v-html="texts.contactFormAcceptText" />
          </label>
        </div>

        <div class="contact-form__submit">
          <button class="btn btn-cta" :class="loading ? 'btn-loader' : ''">
            {{ texts.sendInquiry }}
            <loader :loading="loading" />
          </button>
        </div>
      </form>

      <form-success-message
        v-else-if="formSent"
        :name="name"
        :request-type="requestType"
      />
    </transition>

    <loader :loading="loading" />
  </section>
</template>

<script>
import api from 'GlobalAPI';
import { useContactForm } from 'Composables/useContactForm';
import { showEmailRejection } from 'Composables/useEmailAddressCheck';
import FormInput from 'Components/FormInput.vue';
import FormSuccessMessage from 'Main/contact-form/components/FormSuccessMessage.vue';
import Loader from 'Components/loader/Loader.vue';
import { getLandingPageUrl } from 'Main/visitor';

export default {
  name: 'TombstoneForm',

  components: {
    FormInput,
    FormSuccessMessage,
    Loader,
  },

  setup() {
    const {
      gclid,
      wbraid,
      gbraid,
      oppref,
      hasErrors,
      hasFieldError,
      error,
      triggerFieldError,
      getUtmData,
      getCookieGAVisitorId,
      gtmFormSubmission,
    } = useContactForm();

    return {
      gclid,
      wbraid,
      gbraid,
      oppref,
      hasErrors,
      hasFieldError,
      error,
      triggerFieldError,
      getUtmData,
      getCookieGAVisitorId,
      gtmFormSubmission,
    };
  },

  data() {
    return {
      name: '',
      phone: '',
      email: '',
      deceasedName: '',
      birthYear: '',
      deathYear: '',
      accept: false,
      formSent: false,
      formName: 'contact-form-tombstone',
      texts: theme_vars.contactTexts,
      tombstoneTexts: theme_vars.tombstoneFormTexts,
      loading: false,
      requestType: 'tombstone',
      productName: this.$mountEl.dataset.productName,
      pageTitle: this.$mountEl.dataset.pageTitle
        ? this.$mountEl.dataset.pageTitle
        : '',
      validate: ['name', 'phone', 'email'],
      variationsText: '',
    };
  },

  computed: {
    isDisabled() {
      return !(this.name && this.phone && this.email && this.accept);
    },

    tombstoneMessage() {
      return `${this.texts.tombstoneProductInfo}: ${this.productName}. ${this.variationsText}`;
    },
  },

  methods: {
    /**
     * A hacksy DOM way of getting the variation
     */
    getVariation() {
      /// Get variations
      const variationsForm = document.querySelector('.variations_form');

      if (!variationsForm) {
        return '';
      }

      const variationsArr = JSON.parse(
        variationsForm.dataset.product_variations
      );

      if (!variationsArr) {
        return '';
      }

      // Get selected variation
      const variationId = document.querySelector(
        '#variation-id-container'
      ).value;
      const selectedVariation = variationsArr.filter(
        (variation) => variation.variation_id === parseInt(variationId)
      );

      if (!Object.values || !selectedVariation[0]?.attributes) {
        return '';
      }

      return `Variant: ${Object.values(selectedVariation[0].attributes).join(', ')}`;
    },

    validateFields() {
      this.error = '';
      this.hasFieldError = false;
      this.hasErrors = this.accept === false;

      if (this.hasErrors) {
        this.error = this.texts.mandatoryFieldsError;
      }

      this.validate.forEach((formField) => {
        if (!this.$refs[formField]) {
          return;
        }

        this.$refs[formField].validate();
      });
    },

    async gtm() {
      await this.gtmFormSubmission(
        { formLocation: this.pageTitle, formName: this.formName },
        this.pageTitle,
        this.requestType,
        this.name,
        this.email,
        this.phone
      );
    },

    sendInquiry() {
      if (this.formSent) {
        return;
      }

      this.loading = true;
      this.variationsText = this.getVariation();

      this.validateFields();

      if (this.hasErrors || this.hasFieldError) {
        this.loading = false;
        return;
      }

      const data = {
        contact_name: this.name,
        contact_phone: this.phone,
        contact_email: this.email,
        contact_deceased_name: this.deceasedName,
        contact_birth_year: this.birthYear,
        contact_death_year: this.deathYear,
        contact_request_type: this.requestType,
        contact_via: this.pageTitle,
        contact_gclid: this.gclid,
        contact_wbraid: this.wbraid,
        contact_gbraid: this.gbraid,
        contact_oppref: this.oppref,
        contact_message: this.tombstoneMessage,
        contact_url: window.location.origin + window.location.pathname,
        utm_source: this.getUtmData(),
        ga_id: this.getCookieGAVisitorId(),
        landing_page_url: getLandingPageUrl(),
      };

      api
        .post('send-inquiry', data)
        .then(() => {
          this.loading = false;
          this.formSent = true;
          this.gtm();
        })
        .catch((error) => {
          this.loading = false;

          if (showEmailRejection(error, this.$refs.email)) {
            return;
          }

          this.hasErrors = true;
          this.error = error;
        });
    },
  },
};
</script>
